import { Controller } from "@hotwired/stimulus";

// This Stimulus controller manages dynamic updates for onboarding configuration
export default class extends Controller {
  static targets = [
    "accountWorkflows",
    "accountWorkflowsLink",
    "categoryActive",
    "categoryFilters",
    "categoryInactive",
    "dayOneFormsBox",
    "dayOneFormsLabel",
    "directDepositBox",
    "stateTaxBox",
    "employeeTypeRadio",
    "partnerWorkflows",
    "partnerWorkflowsLink",
    "preloadedWorkflows",
    "preloadedWorkflowsLink",
    "workflowDiv"
  ];

  static values = {
    label: Object,
    directDepositId: String,
    federalW9Id: String,
    federalI9Id: String,
    stateTaxId: String
  };

  connect() {
    this.updateFormLabel();
    this.toggleStateTax();
    this.toggleDirectDeposit();
  }

  // Update form label based on the selected employee type
  updateFormLabel() {
    const selectedType = this.selectedEmployeeType();
    const labelText = this.labelValue[selectedType];
    if (this.hasDayOneFormsLabelTarget) {
      this.dayOneFormsLabelTarget.textContent = labelText;
    }
    this.displayDefaultTaxWorkflow(selectedType);
  }

  // Decide which initial tax workflow to show based on selected employee type
  displayDefaultTaxWorkflow(selectedType) {
    this.toggleTaxFormWorkflow(selectedType);
  }

  // Hide workflow when the remove button is clicked
  removeWorkflow(event) {
    const workflowDiv = event.target.closest("[data-onboarding-config-target='workflowDiv']");
    if (workflowDiv) {
      workflowDiv.classList.add("d-none");
    }
  }

  // Toggle visibility of the state tax workflow based on checkbox
  toggleStateTax() {
    const element = document.getElementById(`workflow-container-${this.stateTaxIdValue}`);
    const isChecked = this.hasStateTaxBoxTarget ? this.stateTaxBoxTarget.checked : false;
    this.toggleElementVisibility(element, isChecked);
  }

  // Toggle visibility of the direct deposit workflow based on checkbox
  toggleDirectDeposit() {
    const element = document.getElementById(`workflow-container-${this.directDepositIdValue}`);
    const isChecked = this.hasDirectDepositBoxTarget ? this.directDepositBoxTarget.checked : false;
    this.toggleElementVisibility(element, isChecked);
  }

  // Toggle visibility of the Day One Tax Forms workflow based on checkbox
  toggleDayOneForms() {
    const selectedType = this.selectedEmployeeType();
    this.toggleTaxFormWorkflow(selectedType);
  }

  // Remove the direct deposit workflow when clicked
  handleDirectDeposit(event) {
    if (this.hasDirectDepositBoxTarget) {
      this.directDepositBoxTarget.checked = false;
    }
    this.removeWorkflow(event);
  }

  // Remove the Day One Tax Forms workflow when clicked
  handleDayOneForms(event) {
    if (this.hasDayOneFormsBoxTarget) {
      this.dayOneFormsBoxTarget.checked = false;
    }
    this.removeWorkflow(event);
  }

  // Remove the State Tax workflow when clicked
  handleStateTax(event) {
    if (this.hasStateTaxBoxTarget) {
      this.stateTaxBoxTarget.checked = false;
    }
    this.removeWorkflow(event);
  }

  // Action triggered on modal click event
  addWorkflows() {
    const addWorkflowsContainer = document.getElementById('add-workflows-container');
    if (addWorkflowsContainer) {
      const checkboxes = addWorkflowsContainer.querySelectorAll('input[type="checkbox"]:checked');
      checkboxes.forEach(checkbox => {
        const taskSetId = checkbox.id.replace('add_workflow_', '');
        this.unhideWorkflow(taskSetId);
      });
    }
  }

  // Toggles the visibility of the "My Workflows" container
  showAccountWorkflows() {
    this.accountWorkflowsTarget.classList.remove("d-none");
    this.partnerWorkflowsTarget.classList.add("d-none");
    this.preloadedWorkflowsTarget.classList.add("d-none");
    this.setActiveTab(this.accountWorkflowsLinkTarget);
  }

  // Toggles the visibility of the "Partnership Workflows" container
  showPartnerWorkflows() {
    this.accountWorkflowsTarget.classList.add("d-none");
    this.partnerWorkflowsTarget.classList.remove("d-none");
    this.preloadedWorkflowsTarget.classList.add("d-none");
    this.setActiveTab(this.partnerWorkflowsLinkTarget);
  }

  // Toggles the visibility of the "Preloaded Workflows" container
  showPreloadedWorkflows() {
    this.accountWorkflowsTarget.classList.add("d-none");
    this.partnerWorkflowsTarget.classList.add("d-none");
    this.preloadedWorkflowsTarget.classList.remove("d-none");
    this.setActiveTab(this.preloadedWorkflowsLinkTarget);
  }

  // Toggle visibility of categories when clicked
  toggleCategoryFilter(event) {
    const button = event.target.closest("button");
    if (!button) return;

    const isActive = button.classList.contains('active');

    // Get the category value from the data attribute (attribute is data-select-button-value)
    const category = button.dataset.selectButtonValue || button.dataset.value;

    // Toggle the category workflow list visibility
    this.toggleCategoryWorkflowList(category, button);

    // Check if any buttons are now active
    this.checkIfAnyButtonActive(isActive);
  }

  private

  // Get the value of the selected employee type radio button
  selectedEmployeeType() {
    const selectedRadio = this.employeeTypeRadioTargets.find(radio => radio.checked);
    return selectedRadio ? selectedRadio.value : null;
  }

  // Toggle tax form workflows based on selected employee type and checkbox state
  toggleTaxFormWorkflow(selectedType) {
    const w9Element = document.getElementById(`workflow-container-${this.federalW9IdValue}`);
    const i9Element = document.getElementById(`workflow-container-${this.federalI9IdValue}`);

    const isChecked = this.hasDayOneFormsBoxTarget ? this.dayOneFormsBoxTarget.checked : false;

    if (selectedType === "contractor") {
      this.toggleElementVisibility(w9Element, isChecked);
      this.toggleElementVisibility(i9Element, false);
    } else {
      this.toggleElementVisibility(i9Element, isChecked);
      this.toggleElementVisibility(w9Element, false);
    }
  }

  // Toggle visibility of an element based on a condition
  toggleElementVisibility(element, isVisible) {
    if (element) {
      element.classList.toggle("d-none", !isVisible);
      this.toggleTaskBoxes(element);
    }
  }

  // Display the workflow when added from the modal
  //
  // Selects all divs with the matching id and only adds if none of the other matchings one are visible
  unhideWorkflow(taskSetId) {
    const workflowDivs = document.querySelectorAll(`#workflow-container-${taskSetId}`);
    const anyVisible = Array.from(workflowDivs).some(workflowDiv => !workflowDiv.classList.contains("d-none"));
    if (!anyVisible) {
      workflowDivs[0].classList.remove("d-none");
      this.toggleTaskBoxes(workflowDivs[0]);
    }
  }

  // Uncheck the boxes for each individual task within a workflow
  toggleTaskBoxes(element) {
    if (element.classList.contains("d-none")) {
      const checkboxes = element.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
    } else {
      const checkboxes = element.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = true;
      });
    }
  }

  // Set the active tab and apply a blue bottom border
  setActiveTab(activeTab) {
    this.accountWorkflowsLinkTarget.classList.remove("active");
    this.partnerWorkflowsLinkTarget.classList.remove("active");
    this.preloadedWorkflowsLinkTarget.classList.remove("active");
    activeTab.classList.add("active");
  }

  checkIfAnyButtonActive(isActive) {
    const activeButtons = document.querySelectorAll('button.active');
    let activeButtonsLength = activeButtons.length;

    // Check if the button clicked was active or not. This is needed for knowing if all the buttons are active of not.
    if (isActive) {
      activeButtonsLength--;
    } else {
      activeButtonsLength++;
    }
    document.getElementById('no-category-accounts').classList.toggle("d-none", activeButtonsLength > 0);
  }

  toggleCategoryWorkflowList(category, button) {
    const accountWorkflowDiv = this.accountWorkflowsTarget.querySelector(`.category-${category}`);
    const partnerWorkflowDiv = this.partnerWorkflowsTarget.querySelector(`.category-${category}`);
    const preloadedWorkflowDiv = this.preloadedWorkflowsTarget.querySelector(`.category-${category}`);
    const isActive = button.classList.contains("active");

    if (accountWorkflowDiv) {
      accountWorkflowDiv.classList.toggle("d-none", isActive);
    }

    if (partnerWorkflowDiv) {
      partnerWorkflowDiv.classList.toggle("d-none", isActive);
    }

    if (preloadedWorkflowDiv) {
      preloadedWorkflowDiv.classList.toggle("d-none", isActive);
    }
  }
}
